import { useEffect, useMemo, useRef, useState } from "react";
import LoadingAnimation from "../../../../assets/svg/LoadingAnimation";
import { useSnackbar } from "notistack";
import dayjs from "@lib/dayjs";
import { useSelector } from "react-redux";
import PaperSendIcon from "../../../../assets/svg/PaperSendIcon";
import useChat from "../../../../custom-hooks/useChat";

const MessagesTab = ({ applicationDetails }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state) => state.auth);
  const receiverDetails = useMemo(
    () => ({
      ...applicationDetails?.candidateDetails,
      _id: applicationDetails?.candidateId,
    }),
    [applicationDetails?.candidateDetails, applicationDetails?.candidateId]
  );

  const { messages, isMessageLoading, sendMessage } = useChat({
    jobApplicationId: applicationDetails?._id,
    receiverDetails,
  });
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const pollingInterval = 3000;

  const messagesContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    if (messages?.length) {
      scrollToBottom();
    }
  }, [messages]);


  if (isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full relative">
      <div
        ref={messagesContainerRef}
        className="flex-grow overflow-y-auto no-scrollbar pr-2 pb-4"
        style={{ maxHeight: "calc(100% - 100px)" }}
      >
        {messages?.length ? (
          messages?.map((item, index) => {
            const isSentByCurrentUser = item.createdBy === user._id;
            return (
              <div
                key={item?.messageId || item?.tempMessageId}
                className="flex flex-col gap-2 rounded cursor-pointer text-xs border-b p-4"
              >
                <div
                  className={`rounded text-primary flex justify-between font-bold`}
                >
                  <p className="text-[13px] font-bold text-primary">
                    {item.createdName}
                  </p>
                  <p className="text-primary font-medium italic">
                    {dayjs(item?.createdAt).format("HH:mm DD/MM/YYYY")}
                  </p>
                </div>
                <div className="flex justify-between items-end">
                  <p
                    className={`text-xs font-medium text-light-black py-3 px-5 rounded`}
                  >
                    {item.message}
                  </p>
                  {isSentByCurrentUser && item.seen && (
                    <p className="text-xss text-green-600">Seen</p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-center flex h-full items-end justify-center pb-10">
            No messages available.
          </p>
        )}
      </div>

      {/* Fixed message input area */}
      <div className="absolute bottom-0 left-0 right-0 bg-white mx-5 mb-2">
        <div className="flex gap-4">
          <textarea
            className="p-4 w-full bg-white rounded text-xs font-medium border border-[#ECECEC] focus:border-gray-300 focus:outline-none"
            rows={4}
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
          <button
            className="text-xs text-primary font-medium flex items-center disabled:cursor-not-allowed"
            onClick={() => sendMessage({ message, setMessage })}
            disabled={isLoading || !message.trim()}
          >
            {isLoading ? (
              <LoadingAnimation color="black" className={"w-4 h-4"} />
            ) : (
              <PaperSendIcon fill={message.trim() && "#FF9800"} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;
